import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { EditTaskSet, TaskSetOptions, TaskSetInfo } from 'src/models/task-set.model';
import { TaskSetService } from 'src/services/task-set.service';

export type TaskSetState = {
  currentEditTaskSet: EditTaskSet | null;
  currentTaskSetOptions: TaskSetOptions | null;
  currentTaskSetInfo: TaskSetInfo | null;
};

export const TaskSetStore = signalStore(
  { providedIn: 'root' },
  withState<TaskSetState>({
    currentEditTaskSet: null,
    currentTaskSetOptions: null,
    currentTaskSetInfo: null,
  }),
  withMethods((store, taskSetService = inject(TaskSetService)) => ({
    setCurrentEditTaskSet(updates: Partial<EditTaskSet>) {
      if (!store.currentEditTaskSet()) return;
      patchState(store, { currentEditTaskSet: { ...store.currentEditTaskSet()!, ...updates } });
    },
    setCurrentTaskSetOptions(updates: Partial<TaskSetOptions>) {
      if (!store.currentTaskSetOptions()) return;
      patchState(store, {
        currentTaskSetOptions: { ...store.currentTaskSetOptions()!, ...updates },
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setCurrentTaskSetInfo(updates: { [key: string]: any }) {
      // TODO: remove the http call here
      if (!store.currentTaskSetInfo()) return;
      taskSetService
        .updateTaskSet(store.currentTaskSetInfo()!.id, {
          ...updates,
        })
        .subscribe(() =>
          patchState(store, { currentTaskSetInfo: { ...store.currentTaskSetInfo()!, ...updates } }),
        );
    },
    fetchTaskSetForEdit(taskSetID: number) {
      taskSetService.getTaskSetForEdit(taskSetID).subscribe((taskSet: EditTaskSet) => {
        patchState(store, { currentEditTaskSet: taskSet });
      });
    },
    fetchTaskSetOptions(taskSetID: number) {
      taskSetService.getTaskSetOptions(taskSetID).subscribe((taskSet: TaskSetOptions) => {
        patchState(store, { currentTaskSetOptions: taskSet });
      });
    },
    fetchTaskSetInfo(taskSetID: number) {
      taskSetService.getTaskSetInfo(taskSetID).subscribe((taskSet: TaskSetInfo) => {
        patchState(store, { currentTaskSetInfo: taskSet });
      });
    },
    clearTaskSetInfo() {
      patchState(store, { currentTaskSetInfo: null });
    },
    clearTaskSetOptions() {
      patchState(store, {
        currentTaskSetOptions: null,
      });
    },
    clearEditTaskSet() {
      patchState(store, { currentEditTaskSet: null });
    },
    clearAllTaskSetData() {
      this.clearEditTaskSet();
      this.clearTaskSetInfo();
      this.clearTaskSetOptions();
    },
    getCurrentTaskSetOptions(taskSetID: number) {
      const options = store.currentTaskSetOptions();
      if (!options) {
        this.fetchTaskSetOptions(taskSetID);
      } else if (options.id !== taskSetID) {
        this.clearTaskSetOptions();
        this.fetchTaskSetOptions(taskSetID);
      }
      return options;
    },
  })),
);
